import { ButtonProps } from '@dfh-corp-sites/ui'
import { graphql } from 'gatsby'
import React, { forwardRef, useMemo } from 'react'

import useSiteFrontPage from '../../hooks/useSiteFrontPage'
import { isAccountType } from '../../lib/auth/signup'
import {
  isSanityLoginLink,
  isSanityRegistrationLink,
  isSanitySupportRequestLink,
  LinkFragment,
  NormalizedSanityLink,
  normalizeSanityInternalHref,
  normalizeSanityLink,
} from '../../lib/sanity/link'
import { logAndCaptureMessage } from '../../utils/errorTools'
import Link, { LinkProps, LinkRef } from './Link'
import LoginLink from './LoginLink'
import RegistrationLink from './RegistrationLink'
import SupportRequestLink from './SupportRequestLink'

const isLinkWithHref = (
  link: Record<string, unknown>,
): link is { href: string; blank?: boolean } => {
  return !!link.href
}

const isLinkFromQuery = (link: Record<string, unknown>): link is LinkFragment => {
  return !!link.target
}

function normalizeLink({
  link,
  frontPage,
}: {
  link: string | LinkFragment | undefined
  frontPage: string | null | undefined
}): NormalizedSanityLink | undefined {
  if (!link) {
    return undefined
  }
  if (typeof link === 'string') {
    return {
      href: link === frontPage ? '/' : link,
    }
  }
  if (typeof link === 'object') {
    if (isLinkWithHref(link)) {
      const { href, blank } = link
      return {
        href,
        blank,
      }
    }
    if (isLinkFromQuery(link)) {
      return normalizeSanityLink({ link, frontPage })
    }
  }
  logAndCaptureMessage(`Could not normalize link: ${JSON.stringify(link)}`)
  return undefined
}

export type NormalizedSanityLinkTagged =
  | { _tag: 'Login' | 'Registration' | 'SupportRequest' }
  | (NormalizedSanityLink & { _tag: 'Normalized' })

export type SanityLinkProps = Omit<LinkProps, 'onClick' | 'to'> & {
  buttonVariantOverride?: ButtonProps['variant']
  /** onClick callback with normalized link in second parameter to support analytics */
  onClick?: (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
    normalizedLink: NormalizedSanityLinkTagged,
  ) => void
  to: LinkProps['to'] | LinkFragment
}

export type SanityLinkRef = LinkRef

/**
 * Link component that renders GatsbyLink component for internal links or <a> element for
 * external links. Accepts `to` argument as string or link fragment from Sanity
 *
 * Since DOM elements <a> cannot receive activeClassName and partiallyActive, destructure
 * the prop here and pass it only to GatsbyLink
 */
const SanityLink = forwardRef<SanityLinkRef, SanityLinkProps>(function SanityLink(
  { buttonVariantOverride, children, onClick, target: targetProp, to, ...otherProps },
  ref,
) {
  // Tailor the following test to your environment.
  // This example assumes that any internal link (intended for Gatsby)
  // will start with exactly one slash, and that anything else is external.
  const siteFrontPage = useSiteFrontPage()

  const memoized = useMemo(() => {
    const normalizedLink = normalizeLink({
      link: to,
      frontPage: normalizeSanityInternalHref({
        href: siteFrontPage?.slug?.current,
        typeName: siteFrontPage?.__typename,
      }),
    })

    if (normalizedLink != null || typeof to !== 'object' || to == null || !isLinkFromQuery(to)) {
      return { normalizedLink }
    }
    const [target] = to.target ?? []
    return {
      normalizedLink,
      loginLink: target && isSanityLoginLink(target) ? target : undefined,
      registrationLink: target && isSanityRegistrationLink(target) ? target : undefined,
      supportRequestLink: target && isSanitySupportRequestLink(target) ? target : undefined,
    }
  }, [siteFrontPage, to])
  const { normalizedLink, loginLink, registrationLink, supportRequestLink } = memoized

  if (loginLink) {
    return (
      <LoginLink
        className={otherProps.className}
        {...(onClick && { onClick: (event) => onClick(event, { _tag: 'Login' }) })}
        redirectPath={loginLink?.redirectPath}
        variant={buttonVariantOverride}
      >
        {children}
      </LoginLink>
    )
  }

  if (registrationLink) {
    const { accountType } = registrationLink
    return (
      <RegistrationLink
        className={otherProps.className}
        accountType={accountType && isAccountType(accountType) ? accountType : undefined}
        {...(onClick && { onClick: (event) => onClick(event, { _tag: 'Registration' }) })}
        variant={buttonVariantOverride}
      >
        {children}
      </RegistrationLink>
    )
  }

  if (supportRequestLink) {
    return (
      <SupportRequestLink
        className={otherProps.className}
        {...(onClick && { onClick: (event) => onClick(event, { _tag: 'SupportRequest' }) })}
      >
        {children}
      </SupportRequestLink>
    )
  }

  const { blank } = normalizedLink ?? {}
  const href = normalizedLink?.href || undefined
  const target = typeof blank === 'boolean' ? (blank ? '_blank' : undefined) : targetProp

  return (
    <Link
      to={href}
      target={target}
      ref={ref}
      {...(onClick && {
        onClick: (event) => onClick(event, { _tag: 'Normalized', ...normalizedLink }),
      })}
      {...otherProps}
    >
      {children}
    </Link>
  )
})

export default SanityLink

export const frontPageFragment = graphql`
  fragment frontPage on SanitySiteSettings {
    frontpage {
      id
      slug {
        current
      }
    }
    dispensaryFrontpage {
      id
      slug {
        current
      }
    }
  }
`

export const query = graphql`
  fragment LinkFragment on SanityLink {
    target {
      ... on SanityInternalLink {
        _type
        reference {
          ... on SanityBlogPost {
            _type
            title
            slug {
              current
            }
          }
          ... on SanityPage {
            _type
            title
            slug {
              current
            }
          }
          ... on SanityResearch {
            _type
            title
            slug {
              current
            }
          }
        }
      }
      ... on SanityPathLink {
        _type
        path
      }
      ... on SanityExternalLink {
        _type
        href
        blank
      }
      ... on SanityLoginLink {
        _type
        redirectPath
      }
      ... on SanitySupportRequestLink {
        _type
      }
      ... on SanityLibraryLink {
        _type
        reference {
          ... on SanityCaseStudy {
            _type
            slug {
              current
            }
          }
          ... on SanityCasiSession {
            _type
            slug {
              current
            }
          }
          ... on SanityCatalogTraining {
            _type
            slug {
              current
            }
          }
          ... on SanityCategoryBrochure {
            _type
            slug {
              current
            }
          }
          ... on SanityJournalArticle {
            _type
            slug {
              current
            }
          }
          ... on SanityLiteratureReference {
            _type
            slug {
              current
            }
          }
          ... on SanityPatientBrochure {
            _type
            slug {
              current
            }
          }
          ... on SanityPatientEducation {
            _type
            slug {
              current
            }
          }
          ... on SanityPriceList {
            _type
            slug {
              current
            }
          }
          ... on SanityPrivateLabelForm {
            _type
            slug {
              current
            }
          }
          ... on SanityProductReferenceChart {
            _type
            slug {
              current
            }
          }
          ... on SanityProtocol {
            _type
            slug {
              current
            }
          }
          ... on SanitySocialMediaResource {
            _type
            slug {
              current
            }
          }
          ... on SanityTechSheet {
            _type
            slug {
              current
            }
          }
          ... on SanityTechSheetFrench {
            _type
            slug {
              current
            }
          }
          ... on SanityWebinar {
            _type
            slug {
              current
            }
          }
          ... on SanityWhitePaper {
            _type
            slug {
              current
            }
          }
        }
      }
      ... on SanityRegistrationLink {
        _type
        accountType
      }
    }
  }
`
